<script>
import ChatHero from '@modules/aawz-intelligence-v2/components/chat-hero/chat-hero.vue'
import SuggestionChips from '@modules/aawz-intelligence-v2/components/suggestion-chips/suggestion-chips.vue'
import SugestoesDeExemplo from '@modules/aawz-intelligence-v2/components/sugestoes-de-exemplo/sugestoes-de-exemplo.vue'
import AcoesPorModulo from '@modules/aawz-intelligence-v2/components/acoes-por-modulo/acoes-por-modulo.vue'
import { HERO_CHAT } from '@modules/aawz-intelligence-v2/constants/TextChat'

export default {
  name: 'IntelligenceV2EmptyMessages',
  components: { ChatHero, SuggestionChips, SugestoesDeExemplo, AcoesPorModulo },
  props: {
    // Primeira linha do herói. Fixa hoje, prop para não precisar tocar aqui
    // quando virar "Bom dia, <nome>".
    saudacao: {
      type: String,
      default: HERO_CHAT.saudacao,
    },
    // Segunda linha do herói — vem do agente selecionado (`Agentes.headline`).
    headline: {
      type: String,
      default: HERO_CHAT.headline,
    },
    // Lista própria de sugestões. Preenchida, o componente NÃO consulta a
    // store do v2 — é o que permite reusar esta composição em painéis com
    // namespace próprio (o Especialista Fee-Based) sem herdar as sugestões do
    // CRM. `null`, o padrão, mantém o caminho antigo intocado.
    sugestoes: {
      type: Array,
      default: null,
    },
    // Grade/lista de ações por módulo, no lugar das pílulas. Chega PREENCHIDA
    // ou não chega: quem decide é a superfície hospedeira, e entrar por prop é
    // o que mantém o Especialista Fee-Based — que reusa esta composição de
    // outro namespace — byte a byte como hoje.
    acoes: {
      type: Array,
      default: function () {
        return []
      },
    },
    densidadeDasAcoes: {
      type: String,
      default: 'compacta',
    },
    // ★ [G-R2-4 do QA da spec-001] Uma metade de `acoes` FALHOU ao carregar
    // — o texto que diz isso, e o rótulo do botão de repetir. Opt-in (`''`):
    // sem eles o bloco é byte a byte o de hoje. Quem sabe o que falhou é a
    // superfície hospedeira; aqui os dois só descem ao `AcoesPorModulo`.
    avisoDasAcoes: {
      type: String,
      default: '',
    },
    avisoDasAcoesAcao: {
      type: String,
      default: '',
    },
    // Layout do `AcoesPorModulo` que desenha `acoes` (abaixo do carrossel do
    // módulo em foco): `'grade'` (padrão, abas + cartões) ou `'lista'` (abas +
    // lista vertical — usado pela lista fixa de módulos do estado vazio da
    // `/ia`). Quem decide é a superfície hospedeira, direto — este componente
    // não deriva o layout de nenhum outro dado.
    layoutDasAcoes: {
      type: String,
      default: 'grade',
    },
    // `carrossel` (padrão) ou `lista` — ver `LAYOUT_DO_MODULO_EM_FOCO` em
    // `views/chat-imersivo/constants.js`. Vale só para o módulo ESCOLHIDO no
    // "+"; a grade de todos os módulos não muda. Default preserva as
    // superfícies que não passam a prop.
    layoutDoModuloEmFoco: {
      type: String,
      default: 'carrossel',
    },
    // Apaga o BLOCO INTEIRO de sugestões — grade, pílulas, spinner e o texto de
    // fallback. Não é "lista vazia": a cascata do template desce um degrau a
    // cada lista que esvazia, então zerar a grade traria as pílulas de volta e
    // zerar as duas mostraria "Não há sugestões agora".
    //
    // ⚠️ Default `false` de propósito: esta composição é reusada FORA do módulo
    // (Especialista Fee-Based, popup, painel do Hub, rota cheia). Quem não
    // passa a prop continua idêntico.
    // Ancora a pilha a uma distância fixa do topo em vez de centrá-la.
    //
    // Sem isto o herói e o composer sobem e descem conforme o que existe
    // embaixo — foi a reclamação tanto do card de cliente quanto do carrossel.
    ancorarNoTopo: {
      type: Boolean,
      default: false,
    },
    // Módulo escolhido no menu do "+". Preenchido, o bloco abaixo da caixa
    // mostra o CARROSSEL das ações dele — mesmo lugar das sugestões.
    moduloEmFoco: {
      type: Object,
      default: null,
    },
    ocultarSugestoes: {
      type: Boolean,
      default: false,
    },
    textoSemSugestoes: {
      type: String,
      default:
        'Não há sugestões agora. Escreva a sua pergunta no campo abaixo, por exemplo: "como está meu escritório?".',
    },
    // 'pilulas' (padrão, `SuggestionChips`) ou 'lista' (estilo ChatGPT,
    // `SugestoesDeExemplo` — ícone + texto, uma sugestão por linha). String
    // literal e não uma constante importada de propósito: este componente é
    // reusado FORA do módulo (Especialista Fee-Based) e não deve depender de
    // um enum de `views/chat-imersivo/constants.js`.
    modoDeSugestao: {
      type: String,
      default: 'pilulas',
    },
  },
  computed: {
    usaSugestoesProprias() {
      return Array.isArray(this.sugestoes)
    },
    promptSuggestions() {
      if (this.usaSugestoesProprias) return this.sugestoes
      return this.$store.getters['aawz-intelligence-v2/promptSuggestions'] || []
    },
    // Curto-circuito antes do getter, e não depois: com a lista vinda por
    // prop o consumidor pode nem ter o módulo do v2 registrado, e ler o
    // getter ali seria uma dependência invisível de um estado alheio.
    loading() {
      if (this.usaSugestoesProprias) return false
      return this.$store.getters[
        'aawz-intelligence-v2/promptSuggestionsLoading'
      ]
    },
    temAcoes() {
      return this.acoes.length > 0
    },
    hasSuggestions() {
      return this.promptSuggestions && this.promptSuggestions.length > 0
    },
  },
  methods: {
    useSuggestion(suggestion) {
      const text = suggestion && suggestion.text ? suggestion.text : suggestion
      this.$emit('use-suggestion', text)
    },
    /**
     * Ponte do teclado entre o composer e as ações.
     *
     * O composer chega por SLOT, então ele e as ações são irmãos que não se
     * enxergam: quem os liga é o pai (`view-chat`), e esta composição só
     * repassa. O caminho de volta é o evento `focar-composer`.
     */
    focarPrimeiraAcao() {
      const acoes = this.$refs.acoesDoModulo
      return Boolean(acoes && acoes.focarPrimeiraAcao())
    },
  },
}
</script>

<template>
  <!-- ⚠️ `justify-center` entra por `:class` e NÃO fixa aqui. Ela é
       `justify-content: center !important`, e centrar a PILHA é o que faz cada
       bloco que aparece (o card do cliente, o carrossel de ações) empurrar o
       herói e o composer de lugar. Em modo ancorado a pilha nasce a uma
       distância fixa do topo e nada mais se desloca sozinho. -->
  <div
    class="empty-messages d-flex flex-column align-center fill-height"
    :class="{
      'justify-center pa-6': !ancorarNoTopo,
      'empty-messages--ancorada': ancorarNoTopo,
    }"
  >
    <div class="empty-messages__coluna d-flex flex-column align-start w-100">
      <ChatHero
        class="empty-messages__hero"
        :saudacao="saudacao"
        :headline="headline"
      />

      <!-- Subline opcional. O herói do v2 é só duas linhas; quem precisa dizer
           de onde vêm os números antes das pílulas (o painel do Fee Fixo)
           entrega o texto aqui em vez de remontar a coluna por fora.
           ⚠️ `$slots` lido AQUI, no render, e não em computed: no Vue 2 ele
           não é reativo, e um computed congelava o primeiro valor — o
           wrapper (e o `gap` dele) não acompanhava o slot chegar ou sair. -->
      <div v-if="$slots.descricao" class="empty-messages__descricao w-100">
        <slot name="descricao" />
      </div>

      <!-- O composer é injetado pelo pai (AC-2.3): no estado vazio ele fica
           DENTRO da composição, abaixo do herói, e não no rodapé da tela. -->
      <div v-if="$slots.composer" class="empty-messages__composer w-100">
        <slot name="composer" />
      </div>

      <!-- O carrossel vence o `ocultarSugestoes`: ele não é sugestão de fundo,
           é a resposta ao que o usuário acabou de escolher no menu. -->
      <AcoesPorModulo
        v-if="moduloEmFoco"
        ref="acoesDoModulo"
        class="w-100"
        :layout="layoutDoModuloEmFoco"
        :modulos="[moduloEmFoco]"
        :densidade="densidadeDasAcoes"
        @use="useSuggestion"
        @voltar="$emit('limpar-ferramenta')"
        @sair-para-o-campo="$emit('focar-composer')"
      />

      <!-- Este bloco IGNORA `ocultarSugestoes`/modo limpo de propósito: ele
           não é sugestão de fundo — desde 10/09/2026 é a lista FIXA de
           módulos do escritório (`ferramentasDoComposer`, com Fee Fixo em
           primeiro) que `view-chat-imersivo.vue` mantém sempre visível no
           estado vazio da `/ia`, em estilo LISTA (`layout-das-acoes="lista"`)
           — decisão definitiva do usuário, não um novo teste A/B (o
           antecessor, cartão quadrado com o catálogo inteiro, foi reprovado
           pelo CTO; o [MODO_DE_SUGESTAO]/[SUGESTOES_FIXAS_DE_EXEMPLO] que
           testava LISTA×GRADE com 3 exemplos genéricos foi retirado). Nesse
           layout as ABAS (chips por módulo — as mesmas do menu "+") ficam
           acima da lista, e o clique numa ação da lista ENVIA o prompt
           (`use`), igual a sempre — nunca "escolhe" o módulo. Quando a visão
           imersiva filtra a lista pelo texto digitado no composer e nada
           casa, `acoes` chega VAZIO e `temAcoes` cai — o bloco inteiro some,
           sem mensagem de fallback (é o mesmo efeito de `ocultarSugestoes`,
           sem precisar dele). -->
      <!-- [feedback do usuário, 06/09] chegou a testar `ampla` (cartão
           branco/opaco em repouso) e voltou atrás — "com fundo branco ficou
           ruim". `densidadeDasAcoes` (COMPACTA por padrão) é a mesma prop
           que o carrossel do "+" já usa: cartão transparente, só fica branco
           no hover. -->
      <!-- ⚠️ `acoes-modulo--proxima-do-input`: este é o ÚNICO consumidor deste
           branch (`temAcoes` só fica `true` quando `view-chat-imersivo.vue`
           alimenta `acoes-por-modulo` — a lista fixa de módulos, sempre
           visível no estado vazio da `/ia`). O carrossel do módulo ESCOLHIDO
           no "+" é o outro `<AcoesPorModulo>` acima (`v-if="moduloEmFoco"`) e
           não leva a classe — o respiro `margin-top: 12px` dele em relação ao
           composer continua o mesmo de sempre. -->
      <!-- `|| avisoDasAcoes`: com o catálogo fora do ar `acoes` pode chegar
           vazio, e o bloco inteiro sumiria levando junto o aviso — que é
           justamente o que o G-R2-4 existe para não deixar acontecer. -->
      <AcoesPorModulo
        v-else-if="temAcoes || avisoDasAcoes"
        ref="acoesDoModulo"
        class="w-100 acoes-modulo--proxima-do-input"
        :modulos="acoes"
        :densidade="densidadeDasAcoes"
        :layout="layoutDasAcoes"
        :aviso="avisoDasAcoes"
        :aviso-acao="avisoDasAcoesAcao"
        @use="useSuggestion"
        @usar-comando="$emit('usar-comando', $event)"
        @aviso-acao="$emit('aviso-das-acoes-acao')"
        @sair-para-o-campo="$emit('focar-composer')"
      />

      <SugestoesDeExemplo
        v-else-if="modoDeSugestao === 'lista' && hasSuggestions"
        class="w-100"
        :sugestoes="promptSuggestions"
        @use="useSuggestion"
      />

      <template v-else-if="!ocultarSugestoes">
        <div v-if="loading" class="d-flex justify-center">
          <v-progress-circular indeterminate color="primary" size="24" />
        </div>

        <SuggestionChips
          v-else-if="hasSuggestions"
          class="w-100"
          :sugestoes="promptSuggestions"
          @use="useSuggestion"
        />

        <!-- Terceiro estado: sem sugestões. Sem ele, a falha de carga deixa a
           tela quase vazia e o usuário sem saber que pode simplesmente
           escrever (T-2c). -->
        <div v-else class="sem-sugestoes az-text-secondary az-size-12 az-muted">
          {{ textoSemSugestoes }}
        </div>
      </template>

      <!-- Rodapé da coluna, DEPOIS das pílulas: é onde a tela cheia pendura o
           painel do dia (agenda + tarefas). Opt-in — as outras quatro
           superfícies do estado vazio não passam nada e não mudam. -->
      <div v-if="$slots.rodape" class="empty-messages__rodape w-100">
        <slot name="rodape" />
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
// ⚠️ O `pa-6` também sai por `:class` neste modo. Ele é `padding: 24px
// !important`, empata em especificidade com esta classe e vence por ordem no
// bundle — o `padding-top` daqui simplesmente não aplicava. Quinta vez que uma
// utilitária da Vuetify entra no caminho desta feature; a saída é sempre a
// mesma: não aplicar a classe, em vez de somar `!important`.
//
// Distância do topo MEDIDA no browser, não estimada: com 18vh a pilha ficava
// 155px acima do centro da área e a tela lia como desequilibrada. 28vh deixa o
// conjunto ~60px acima do centro — acima, como na referência, mas centrado o
// bastante para não parecer que o conteúdo escorregou para cima.
//
// `justify-content: flex-start` vem da ausência do `justify-center`; não
// precisa ser declarado.
.empty-messages--ancorada
  padding: 28vh 24px 24px
  // Só a posição (ancorado no topo, não centralizado) — a LARGURA volta a
  // seguir a regra padrão de `.empty-messages__coluna` logo abaixo (a
  // `--vazio`, mais estreita). Decisão de 31/08/2026: antes disso a coluna
  // aqui forçava `--ia-chat-trilho-largura` (a mesma do cartão de cliente e
  // do trilho de mensagens) de propósito, pra composer/cartão não mudarem de
  // largura quando a conversa começa — o `--ia-chat-trilho-largura` cresceu
  // (900px → 1024px) e o vazio ficou largo demais pra uma caixa sozinha sem
  // conteúdo. Trade-off aceito na época: o composer PULA de largura ao enviar
  // a 1ª mensagem (672px → 1024px). ⚠️ O SEGUNDO efeito colateral aceito ali —
  // o cartão de cliente em foco ficar mais largo que a caixa abaixo dele
  // enquanto não há conversa — foi corrigido em 10/09/2026: a prop `estreito`
  // de `chat-cliente-escopo.vue` (ligada a `!hasMessages` em `view-chat.vue`)
  // agora acompanha esta MESMA coluna vazia, e só volta ao trilho cheio junto
  // com o composer, quando a conversa começa.

.empty-messages__coluna
  // 672px = max-w-2xl do protótipo (§7.3). A coluna do estado vazio é mais
  // estreita que a das mensagens (768px) de propósito — token
  // `--ia-chat-trilho-largura-vazio` (`design/_ia-chat-trilho.sass`), fonte
  // única também para as outras 4 superfícies do estado vazio.
  //
  // `align-start` no template, não `align-center`: no protótipo o título, o
  // composer e os chips compartilham a MESMA borda esquerda. A coluna em si
  // segue centrada na tela — o que alinha à esquerda é o conteúdo dentro dela.
  max-width: var(--ia-chat-trilho-largura-vazio)
  // 32 e não 24: com o painel do dia embaixo a coluna passou a empilhar cinco
  // blocos (herói, composer, dica, pílulas, cartões), e no ritmo anterior eles
  // liam como um bloco só. ⚠️ Vale para as CINCO superfícies do estado vazio —
  // se o popup de 700px ou o painel do Hub de 420px ficarem soltos demais, o
  // ajuste é uma variante por superfície, não voltar todo mundo para 24.
  gap: 32px


// ★ O rodapé da coluna ANCORADA desce até o fim (17/09/2026): a leitura do
// estado vazio da `/home-ia` é herói → composer → (espaço vazio) → cartão de
// suporte, com o aviso regulatório logo abaixo, fora desta composição
// (`view-chat.vue`). Quem faz isso é o `margin-top: auto` do rodapé dentro de
// uma coluna que CRESCE — daí o `flex: 1` aqui.
//
// ⚠️ Escopado em `--ancorada`, que só a superfície imersiva liga: no popup, no
// painel do Hub e no Fee-Based a coluna continua sendo do tamanho do conteúdo,
// e o rodapé (o painel do dia) segue colado nas pílulas como hoje.
//
// Em viewport baixo o espaço livre é zero, as margens automáticas resolvem em
// zero e sobra o `gap: 32px` da própria coluna — nunca uma rolagem nova.
.empty-messages--ancorada
  .empty-messages__coluna
    flex: 1 1 auto
    min-height: 0

  .empty-messages__rodape
    margin-top: auto
    // Sobe o cartão do chão: sem isto o vão até o aviso regulatório é só o
    // `padding-bottom` da coluna (24px) mais o `pt-2` do aviso (8px), e o
    // cartão ficava colado nele. 32px dobram esse respiro.
    margin-bottom: 32px

.sem-sugestoes
  max-width: 420px
</style>
